import { z } from "zod";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";

export interface GeneralLedgerFilterPurpose {
    role: AccountRole;
    label: string;
    tooltip?: string;
}

export interface GeneralLedgerFilterTypeNode {
    type: AccountType;
    purposes: readonly GeneralLedgerFilterPurpose[];
}

export const GENERAL_LEDGER_FILTER_TREE: readonly GeneralLedgerFilterTypeNode[] =
    [
        {
            type: AccountType.cash,
            purposes: [
                {
                    role: AccountRole.cashOnHand,
                    label: "Cash on Hand",
                    tooltip:
                        "The account Kick uses for manually recorded cash payments on bills and invoices. Account name per client may vary.",
                },
                {
                    role: AccountRole.bankTransferClearing,
                    label: "Bank Transfer Clearing",
                    tooltip:
                        "The in-transit account Kick uses when matching transfers between bank accounts. Account name per client may vary.",
                },
                {
                    role: AccountRole.paymentTransferClearing,
                    label: "Payment Transfer Clearing",
                    tooltip:
                        "The in-transit account Kick uses when matching transfers to or from a payment processor. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.accountsReceivable,
            purposes: [
                {
                    role: AccountRole.accountsReceivable,
                    label: "Accounts Receivable",
                    tooltip:
                        "The account Kick uses as the default for customer invoice balances. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.inventory,
            purposes: [],
        },
        {
            type: AccountType.otherCurrentAssets,
            purposes: [],
        },
        {
            type: AccountType.investments,
            purposes: [],
        },
        {
            type: AccountType.fixedAssets,
            purposes: [],
        },
        {
            type: AccountType.intangibleAssets,
            purposes: [],
        },
        {
            type: AccountType.otherAssets,
            purposes: [
                {
                    role: AccountRole.intercompanyReceivable,
                    label: "Intercompany Receivables",
                    tooltip:
                        "The fallback account Kick uses for amounts owed from related businesses when there isn't a dedicated due-to/from account. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.accountsPayable,
            purposes: [
                {
                    role: AccountRole.accountsPayable,
                    label: "Accounts Payable",
                    tooltip:
                        "The account Kick uses as the default for vendor bill balances. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.creditCards,
            purposes: [
                {
                    role: AccountRole.creditCardClearing,
                    label: "Credit Card Clearing",
                    tooltip:
                        "The in-transit account Kick uses when recording credit card payments. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.payrollLiabilities,
            purposes: [],
        },
        {
            type: AccountType.shortTermLoans,
            purposes: [],
        },
        {
            type: AccountType.otherCurrentLiabilities,
            purposes: [
                {
                    role: AccountRole.salesTaxPayable,
                    label: "Sales Tax Payable",
                    tooltip:
                        "The account Kick uses for sales tax collected on invoices. Account name per client may vary.",
                },
                {
                    role: AccountRole.customerDeposits,
                    label: "Customer Deposits",
                    tooltip:
                        "The account Kick uses for customer deposits and prepayments applied on invoices. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.longTermLoans,
            purposes: [],
        },
        {
            type: AccountType.otherLiabilities,
            purposes: [
                {
                    role: AccountRole.intercompanyPayable,
                    label: "Intercompany Payables",
                    tooltip:
                        "The fallback account Kick uses for amounts owed to related businesses when there isn't a dedicated due-to/from account. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.equity,
            purposes: [
                {
                    role: AccountRole.ownerContributions,
                    label: "Owner Investments / Contributions",
                    tooltip:
                        "The account Kick uses when you move a transaction between personal and business, and when you match an incoming transfer to personal. Account name per client may vary.",
                },
                {
                    role: AccountRole.ownerDraws,
                    label: "Owner Draws / Distributions",
                    tooltip:
                        "The account Kick uses when you move a transaction between business and personal, and when you match an outgoing transfer to personal. Account name per client may vary.",
                },
                {
                    role: AccountRole.retainedEarningsOrOwnersCapital,
                    label: "Owner Capital / Capital / Retained Earnings",
                    tooltip:
                        "The equity account Kick uses for accumulated earnings on the Balance Sheet. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.income,
            purposes: [
                {
                    role: AccountRole.defaultRevenue,
                    label: "Revenue",
                    tooltip:
                        "The default income account Kick uses for invoice line items and transactions. Account name per client may vary.",
                },
                {
                    role: AccountRole.discountsAndAllowances,
                    label: "Discounts and Allowances",
                    tooltip:
                        "The contra-revenue account Kick uses for invoice discounts and allowances. Account name per client may vary.",
                },
                {
                    role: AccountRole.uncategorizedIncome,
                    label: "Uncategorized Income",
                    tooltip:
                        "The account Kick uses for cash in activity that hasn't been categorized yet. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.cogs,
            purposes: [],
        },
        {
            type: AccountType.operatingExpenses,
            purposes: [
                {
                    role: AccountRole.payrollClearing,
                    label: "Salaries and Wages - Clearing",
                    tooltip:
                        "The in-transit account Kick uses for payroll activity. Account name per client may vary.",
                },
                {
                    role: AccountRole.uncategorizedExpense,
                    label: "Uncategorized Expenses",
                    tooltip:
                        "The account Kick uses for cash out activity that hasn't been categorized yet. Account name per client may vary.",
                },
            ],
        },
        {
            type: AccountType.otherIncome,
            purposes: [],
        },
        {
            type: AccountType.otherExpenses,
            purposes: [
                {
                    role: AccountRole.badDebtExpense,
                    label: "Bad Debts",
                    tooltip:
                        "The expense account Kick uses when customer balances are written off as uncollectible. Account name per client may vary.",
                },
            ],
        },
    ];

const GENERAL_LEDGER_TYPE_VALUES: ReadonlySet<string> = new Set(
    GENERAL_LEDGER_FILTER_TREE.map((node) => node.type),
);

const GENERAL_LEDGER_ROLE_VALUES: ReadonlySet<string> = new Set(
    GENERAL_LEDGER_FILTER_TREE.flatMap((node) =>
        node.purposes.map((purpose) => purpose.role),
    ),
);

const GENERAL_LEDGER_ROLE_TO_TYPE: ReadonlyMap<AccountRole, AccountType> =
    new Map(
        GENERAL_LEDGER_FILTER_TREE.flatMap((node) =>
            node.purposes.map((purpose): [AccountRole, AccountType] => [
                purpose.role,
                node.type,
            ]),
        ),
    );

const GENERAL_LEDGER_ROLE_TO_LABEL: ReadonlyMap<AccountRole, string> = new Map(
    GENERAL_LEDGER_FILTER_TREE.flatMap((node) =>
        node.purposes.map((purpose): [AccountRole, string] => [
            purpose.role,
            purpose.label,
        ]),
    ),
);

const GENERAL_LEDGER_ROLE_TO_TOOLTIP: ReadonlyMap<AccountRole, string> =
    new Map(
        GENERAL_LEDGER_FILTER_TREE.flatMap((node) =>
            node.purposes.flatMap((purpose): Array<[AccountRole, string]> =>
                purpose.tooltip ? [[purpose.role, purpose.tooltip]] : [],
            ),
        ),
    );

export function isGeneralLedgerFilterRole(value: string): value is AccountRole {
    return GENERAL_LEDGER_ROLE_VALUES.has(value);
}

export function isGeneralLedgerFilterType(value: string): value is AccountType {
    return GENERAL_LEDGER_TYPE_VALUES.has(value);
}

/**
 * Zod schemas constrained to the general-ledger subset of `AccountRole` /
 * `AccountType` in `GENERAL_LEDGER_FILTER_TREE`. Out-of-scope enum members fail
 * validation instead of being silently dropped later at resolution time.
 */
export const generalLedgerFilterRoleSchema = z
    .nativeEnum(AccountRole)
    .refine(isGeneralLedgerFilterRole);

export const generalLedgerFilterTypeSchema = z
    .nativeEnum(AccountType)
    .refine(isGeneralLedgerFilterType);

export type GeneralLedgerFilterTarget = "cash" | "accrual";

export const GENERAL_LEDGER_FILTER_KEYS = {
    cash: {
        roles: "cashGeneralLedgerRoles",
        types: "cashGeneralLedgerTypes",
        resolvedAccounts: "coaAccounts",
    },
    accrual: {
        roles: "accrualGeneralLedgerRoles",
        types: "accrualGeneralLedgerTypes",
        resolvedAccounts: "accrualCoaAccounts",
    },
} as const;

export const ALL_GENERAL_LEDGER_FILTER_KEYS = [
    GENERAL_LEDGER_FILTER_KEYS.cash.roles,
    GENERAL_LEDGER_FILTER_KEYS.cash.types,
    GENERAL_LEDGER_FILTER_KEYS.accrual.roles,
    GENERAL_LEDGER_FILTER_KEYS.accrual.types,
] as const;

export function getGeneralLedgerTypeNodeId(type: AccountType): string {
    return `type:${type}`;
}

export function readGeneralLedgerTypeFromNodeId(
    id: string,
): AccountType | undefined {
    const prefix = "type:";
    if (!id.startsWith(prefix)) {
        return undefined;
    }
    const type = id.slice(prefix.length);
    if (!isGeneralLedgerFilterType(type)) {
        return undefined;
    }
    return type;
}

export function readGeneralLedgerRoles(value: unknown): AccountRole[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(
        (role): role is AccountRole =>
            typeof role === "string" && isGeneralLedgerFilterRole(role),
    );
}

export function readGeneralLedgerTypes(value: unknown): AccountType[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(
        (type): type is AccountType =>
            typeof type === "string" && isGeneralLedgerFilterType(type),
    );
}

export function getGeneralLedgerRoleLabel(role: AccountRole): string {
    return GENERAL_LEDGER_ROLE_TO_LABEL.get(role) ?? role;
}

export function getGeneralLedgerRoleTooltip(
    role: AccountRole,
): string | undefined {
    return GENERAL_LEDGER_ROLE_TO_TOOLTIP.get(role);
}

export function getAccountTypeForGeneralLedgerRole(
    role: AccountRole,
): AccountType | undefined {
    return GENERAL_LEDGER_ROLE_TO_TYPE.get(role);
}

export interface GeneralLedgerFilterAccount {
    id: string;
    type: AccountType;
    role?: AccountRole | null;
    entityId?: number;
}

interface ResolveGeneralLedgerAccountIdsParams {
    accounts: readonly GeneralLedgerFilterAccount[];
    roles?: readonly AccountRole[];
    types?: readonly AccountType[];
    entityIds?: readonly number[];
}

function accountIsInEntityScope(
    account: GeneralLedgerFilterAccount,
    entityIdSet: ReadonlySet<number> | undefined,
): boolean {
    if (!entityIdSet || account.entityId == null) {
        return true;
    }
    return entityIdSet.has(account.entityId);
}

export function resolveGeneralLedgerAccountIds({
    accounts,
    roles = [],
    types = [],
    entityIds,
}: ResolveGeneralLedgerAccountIdsParams): string[] {
    if (roles.length === 0 && types.length === 0) {
        return [];
    }

    const selectedRoles = new Set(roles);
    const selectedTypes = new Set(types);
    const entityIdSet =
        entityIds && entityIds.length > 0 ? new Set(entityIds) : undefined;

    const ids: string[] = [];
    for (const account of accounts) {
        if (!accountIsInEntityScope(account, entityIdSet)) {
            continue;
        }
        if (selectedTypes.has(account.type)) {
            ids.push(account.id);
            continue;
        }
        if (account.role == null || !selectedRoles.has(account.role)) {
            continue;
        }
        const expectedType = GENERAL_LEDGER_ROLE_TO_TYPE.get(account.role);
        if (expectedType != null && account.type !== expectedType) {
            continue;
        }
        ids.push(account.id);
    }
    return [...new Set(ids)];
}

interface SelectedFromCoaAccountsParams {
    accounts: readonly GeneralLedgerFilterAccount[];
    coaAccountIds: readonly string[];
    entityIds?: readonly number[];
}

export function getSelectedGeneralLedgerRolesFromCoaAccounts({
    accounts,
    coaAccountIds,
    entityIds,
}: SelectedFromCoaAccountsParams): AccountRole[] {
    const selectedIds = new Set(coaAccountIds);
    const entityIdSet =
        entityIds && entityIds.length > 0 ? new Set(entityIds) : undefined;
    const selectedRoles: AccountRole[] = [];

    for (const node of GENERAL_LEDGER_FILTER_TREE) {
        for (const purpose of node.purposes) {
            const matching = accounts.filter((account) => {
                if (
                    account.role !== purpose.role ||
                    account.type !== node.type
                ) {
                    return false;
                }
                return accountIsInEntityScope(account, entityIdSet);
            });
            if (
                matching.length > 0 &&
                matching.every((account) => selectedIds.has(account.id))
            ) {
                selectedRoles.push(purpose.role);
            }
        }
    }

    return selectedRoles;
}

export function getSelectedGeneralLedgerTypesFromCoaAccounts({
    accounts,
    coaAccountIds,
    entityIds,
}: SelectedFromCoaAccountsParams): AccountType[] {
    const selectedIds = new Set(coaAccountIds);
    const entityIdSet =
        entityIds && entityIds.length > 0 ? new Set(entityIds) : undefined;
    const selectedTypes: AccountType[] = [];

    for (const node of GENERAL_LEDGER_FILTER_TREE) {
        const matching = accounts.filter((account) => {
            if (account.type !== node.type) {
                return false;
            }
            return accountIsInEntityScope(account, entityIdSet);
        });
        if (
            matching.length > 0 &&
            matching.every((account) => selectedIds.has(account.id))
        ) {
            selectedTypes.push(node.type);
        }
    }

    return selectedTypes;
}
